import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  Alert,
  CircularProgress,
  ButtonGroup,
  Button,
  TextField
} from '@mui/material';
import {
  Home,
  Business,
  Pool,
  Construction,
  CleaningServices,
  Landscape,
  AirOutlined as AirIcon,
  Storage,
  Kitchen,
  Window,
  Kitchen as FridgeIcon,
  LocalLaundryService,
  Brush,
  Straighten,
  BlindOutlined,
  Add as AddIcon,
  Remove as RemoveIcon
} from '@mui/icons-material';
import cleaningServiceCatalog from '../../services/cleaningServiceCatalog';

// Icon mapping for services (fallback when no icon from API)
const SERVICE_ICON_MAPPING = {
  'General Cleaning': Home,
  'Deep Cleaning': CleaningServices,
  'Move In/Move Out Cleaning': Home,
  'Office/Commercial Cleaning': Business,
  'Post-Construction Cleaning': Construction,
  'Exteriors Cleaning': Landscape,
  'Pool Cleaning': Pool
};

// Icon mapping for extra services (fallback when no icon from API)
const EXTRA_ICON_MAPPING = {
  'ceiling-fans': AirIcon,
  'home-organization': Storage,
  'inside-cabinets': Kitchen,
  'inside-windows': Window,
  'inside-fridge': FridgeIcon,
  'inside-oven': Kitchen,
  'load-of-laundry': LocalLaundryService,
  'wall-cleaning': Brush,
  'wet-wipe-baseboards': Straighten,
  'wet-wipe-blinds': BlindOutlined
};

const ServiceSelectionComponent = ({
  onServiceSelect,
  onExtraServicesChange,
  selectedService,
  selectedExtras = [],
  extraQuantities = {},
  onExtraQuantityChange,
  readOnly = false,
  showPricing = true,
  compact = false
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentService, setCurrentService] = useState(null);
  const [services, setServices] = useState([]);
  const [extraServices, setExtraServices] = useState([]);

  // Fetch services using the cleaningServiceCatalog service
  useEffect(() => {
    const fetchServices = async () => {
      setLoading(true);
      try {
        const tenantId = process.env.REACT_APP_TENANT_ID || '1';
        const cityCode = process.env.REACT_APP_CITY_CODE || 'MCO';

        // Fetch main services using the new service
        const mainServices = await cleaningServiceCatalog.getMainServices(tenantId, cityCode);
        setServices(mainServices);

        // If there's a selected service, find it in the fetched services
        if (selectedService) {
          const service = mainServices.find(s => s.catalogID === selectedService || s.serviceId === selectedService);
          setCurrentService(service);
          
          // Fetch compatible extras for the selected service
          if (service) {
            try {
              const compatibleExtras = await cleaningServiceCatalog.getCompatibleExtras(service.name, tenantId, cityCode);
              setExtraServices(compatibleExtras);
            } catch (extraErr) {
              console.warn('Could not fetch compatible extras for service:', service.name, extraErr);
              setExtraServices([]); // Set empty array on error
            }
          }
        }
      } catch (err) {
        console.error('Error fetching services:', err);
        setError('Failed to load services. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, [selectedService]);

  const handleServiceSelect = async (service) => {
    if (readOnly) return;
    setCurrentService(service);
    
    // Fetch compatible extras for the newly selected service
    try {
      const tenantId = process.env.REACT_APP_TENANT_ID || '1';
      const cityCode = process.env.REACT_APP_CITY_CODE || 'MCO';
      
      const compatibleExtras = await cleaningServiceCatalog.getCompatibleExtras(service.name, tenantId, cityCode);
      setExtraServices(compatibleExtras);
    } catch (err) {
      console.warn('Could not fetch compatible extras for service:', service.name, err);
      // Continue with empty extras on error
      setExtraServices([]);
    }
    
    onServiceSelect(service);
  };

  const handleExtraServiceToggle = (extraId) => {
    if (readOnly) return;
    
    console.log('handleExtraServiceToggle:', {
      extraId,
      currentSelectedExtras: selectedExtras,
      currentQuantities: extraQuantities
    });

    const isCurrentlySelected = selectedExtras.includes(extraId);
    let newSelectedIds;
    
    if (isCurrentlySelected) {
      // Remove the service
      newSelectedIds = selectedExtras.filter(id => id !== extraId);
    } else {
      // Add the service
      newSelectedIds = [...selectedExtras, extraId];
      onExtraQuantityChange?.(extraId, 1); // Start with 1 as default quantity
    }
    
    // Always pass full service objects with complete details
    const allSelectedServices = newSelectedIds.map(id => {
      const service = extraServices.find(e => e.catalogID === id);
      if (service) {
        return {
          ...service,
          quantity: extraQuantities[id] || 1
        };
      } else {
        // Fallback - should not happen if extraServices is properly loaded
        console.warn(`Service with catalogID ${id} not found in extraServices`);
        return {
          catalogID: id,
          quantity: extraQuantities[id] || 1
        };
      }
    });
    
    console.log('handleExtraServiceToggle - Sending complete service objects:', allSelectedServices);
    onExtraServicesChange(allSelectedServices);
  };

  const handleQuantityChange = (extraId, newValue, e) => {
    if (readOnly) return;
    if (e) e.stopPropagation();
    
    const extra = extraServices.find(e => e.catalogID === extraId);
    if (!extra) {
      console.warn('Extra service not found:', extraId);
      return;
    }

    // Parse the value as a float for decimal steps
    const parsedValue = parseFloat(newValue);
    if (isNaN(parsedValue)) {
      console.warn('Invalid quantity value:', newValue);
      return;
    }

    // Ensure the value is within bounds
    const value = Math.max(1, Math.min(999, parsedValue));
    
    console.log('handleQuantityChange:', {
      extraId,
      newValue,
      parsedValue,
      finalValue: value,
      extraInfo: extra
    });

    onExtraQuantityChange?.(extraId, value);
  };

  const renderServiceCard = (service) => {
    const isSelected = currentService?.catalogID === service.catalogID;
    
    // Get icon from mapping or use default
    const IconComponent = SERVICE_ICON_MAPPING[service.name] || CleaningServices;

    return (
      <Card
        sx={{
          height: '100%',
          border: isSelected ? 2 : 1,
          borderColor: isSelected ? 'primary.main' : 'grey.300',
          cursor: readOnly ? 'default' : 'pointer',
          '&:hover': !readOnly ? {
            borderColor: 'primary.main',
            transform: 'translateY(-2px)',
            transition: 'all 0.2s'
          } : {}
        }}
      >
        <CardActionArea
          onClick={() => handleServiceSelect(service)}
          disabled={readOnly}
          sx={{ height: '100%' }}
        >
          <CardContent>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <IconComponent color="primary" sx={{ fontSize: 40 }} />
            </Box>
            <Typography variant="h6" gutterBottom>
              {service.name}
            </Typography>
            {!compact && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {service.description}
              </Typography>
            )}
            {showPricing && (
              <Typography variant="h6" color="primary.main">
                Starting at ${service.rate}
              </Typography>
            )}
          </CardContent>
        </CardActionArea>
      </Card>
    );
  };

  const renderExtraServiceCard = (extra) => {
    const extraId = extra.catalogID;
    const isSelected = selectedExtras.includes(extraId);
    const isCompatible = true; // Compatible extras are already filtered by the service
    const quantity = extraQuantities[extraId] || 1;

    console.log('renderExtraServiceCard:', {
      extraId,
      isSelected,
      quantity
    });

    const totalPrice = (extra.rate * quantity).toFixed(2);
    
    // Get icon from mapping or use default
    const IconComponent = EXTRA_ICON_MAPPING[extra.serviceId] || CleaningServices;

    return (
      <Card
        sx={{
          opacity: !currentService || isCompatible ? 1 : 0.5,
          border: isSelected ? 2 : 1,
          borderColor: isSelected ? 'primary.main' : 'grey.300',
          cursor: readOnly || !isCompatible ? 'default' : 'pointer',
          '&:hover': !readOnly && isCompatible ? {
            borderColor: 'primary.main',
            transform: 'translateY(-2px)',
            transition: 'all 0.2s'
          } : {}
        }}
      >
        <CardActionArea
          onClick={() => isCompatible && handleExtraServiceToggle(extraId)}
          disabled={readOnly || !isCompatible}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <IconComponent color="primary" sx={{ mr: 1 }} />
              <Typography variant="subtitle1">
                {extra.name}
              </Typography>
            </Box>
            {!compact && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {extra.description}
              </Typography>
            )}
            {showPricing && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="primary.main">
                  ${totalPrice} {extra.unit && `${extra.unit}`}
                </Typography>
              </Box>
            )}
            {isSelected && (
              <Box 
                sx={{ mt: 2 }}
                onClick={(e) => e.stopPropagation()}
              >
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  Quantity{extra.unit && ` (${extra.unit})`}:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <ButtonGroup size="small">
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleQuantityChange(extraId, quantity - 1, e);
                      }}
                      disabled={quantity <= 1}
                    >
                      <RemoveIcon fontSize="small" />
                    </Button>
                    <TextField
                      size="small"
                      value={quantity}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleQuantityChange(extraId, e.target.value, e);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      inputProps={{
                        style: { textAlign: 'center', width: '50px' },
                        min: 1,
                        max: 999
                      }}
                    />
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleQuantityChange(extraId, quantity + 1, e);
                      }}
                      disabled={quantity >= 999}
                    >
                      <AddIcon fontSize="small" />
                    </Button>
                  </ButtonGroup>
                </Box>
              </Box>
            )}
            {isSelected && (
              <Chip
                label="Selected"
                color="primary"
                size="small"
                sx={{ mt: 1 }}
              />
            )}
          </CardContent>
        </CardActionArea>
      </Card>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Main Services */}
      <Typography variant="h6" gutterBottom>
        Select Service Type
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {services.map((service) => (
          <Grid item xs={12} sm={6} md={compact ? 4 : 3} key={service.catalogID}>
            {renderServiceCard(service)}
          </Grid>
        ))}
      </Grid>

      {/* Extra Services */}
      {currentService && (
        <>
          <Typography variant="h6" gutterBottom>
            Additional Services
          </Typography>
          <Grid container spacing={2}>
            {extraServices.map((extra) => (
              <Grid item xs={12} sm={6} md={compact ? 4 : 3} key={extra.catalogID}>
                {renderExtraServiceCard(extra)}
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Box>
  );
};

export default ServiceSelectionComponent; 